/**
 * Pixel24 — homepage "How it works" interactive build walkthrough.
 *
 * Steps through 6 build stages, each with its own build photo and a caption panel
 * (label/title/body). See snippets/pixel24-how-it-works.liquid for the markup this drives.
 *
 * Autoplay starts once the section scrolls >35% into view (IntersectionObserver), advancing
 * one stage every 2300ms; any manual interaction (arrow/dot click, hovered keyboard arrows,
 * touch swipe) permanently stops it, mirroring the scroll-triggered/stop-on-interact pattern
 * in pixel24-overworld.js. Disabled entirely under prefers-reduced-motion.
 */
(function () {
  var STAGE_COUNT = 6;
  var HUD_TEXT = ['EMPTY PLATE', '25% BUILT', '50% BUILT', '75% BUILT', 'COMPLETE', 'FRAMED'];
  var LABELS = ['Stage 1 / 6', 'Stage 2 / 6', 'Stage 3 / 6', 'Stage 4 / 6', 'Stage 5 / 6', 'Stage 6 / 6'];
  var TITLES = [
    'Start with an empty plate',
    'Place your first pixels',
    'Watch it come together',
    'Finish the last stretch',
    'Complete your artwork',
    'Display your masterpiece'
  ];
  var BODIES = [
    'Every build starts with a clean plate. Line it up with your guide and get ready to place your first numbered pixel.',
    'Press pixels into place one by one. No glue, no cutting. Your artwork starts to take shape.',
    'Keep going and the picture fills in fast. Every finished section makes the reveal more satisfying.',
    'Three quarters in, the character is unmistakable. The last colours go down quickest — you can see exactly where each one belongs.',
    'Most builds take about 20–40 minutes. Take a second to admire the finished piece before you frame it.',
    'Snap the artwork into its wooden frame and hang it anywhere. A piece you built yourself, ready to show off.'
  ];
  var AUTOPLAY_INTERVAL = 5300;
  var SWIPE_THRESHOLD = 44;

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initTutorial(root) {
    var sceneEl = root.querySelector('[data-hiw-scene]');
    var images = root.querySelectorAll('[data-hiw-image]');
    var hudEl = root.querySelector('[data-hiw-hud]');
    var labelEl = root.querySelector('[data-hiw-label]');
    var titleEl = root.querySelector('[data-hiw-title]');
    var bodyEl = root.querySelector('[data-hiw-body]');
    var ctaEl = root.querySelector('[data-hiw-cta]');
    var prevBtn = root.querySelector('[data-hiw-prev]');
    var nextBtn = root.querySelector('[data-hiw-next]');
    var dots = root.querySelectorAll('[data-hiw-dot]');
    if (!sceneEl || !images.length) { return; }

    var state = { stage: 0 };
    var interacted = false;
    var started = false;
    var timer = null;

    function render() {
      var stage = state.stage;
      root.setAttribute('data-hiw-stage', String(stage));
      Array.prototype.forEach.call(images, function (img, i) {
        img.classList.toggle('is-active', i === stage);
      });
      if (hudEl) { hudEl.textContent = HUD_TEXT[stage]; }
      if (labelEl) { labelEl.textContent = LABELS[stage]; }
      if (titleEl) { titleEl.textContent = TITLES[stage]; }
      if (bodyEl) { bodyEl.firstChild ? (bodyEl.firstChild.nodeValue = BODIES[stage]) : bodyEl.textContent = BODIES[stage]; }
      if (ctaEl) { ctaEl.hidden = stage !== STAGE_COUNT - 1; }
      sceneEl.setAttribute('aria-label', 'Build walkthrough, stage ' + (stage + 1) + ' of ' + STAGE_COUNT);
      Array.prototype.forEach.call(dots, function (dot, i) {
        dot.classList.toggle('is-active', i === stage);
      });
    }

    function goTo(i) {
      state.stage = Math.max(0, Math.min(STAGE_COUNT - 1, i));
      render();
    }
    function next() { goTo(state.stage + 1); }
    function prev() { goTo(state.stage - 1); }

    function stopAuto() {
      interacted = true;
      if (timer) { window.clearInterval(timer); timer = null; }
    }

    function startAuto() {
      if (started || interacted || reduceMotion) { return; }
      started = true;
      timer = window.setInterval(function () {
        if (state.stage >= STAGE_COUNT - 1 || interacted) {
          window.clearInterval(timer);
          timer = null;
          return;
        }
        next();
      }, AUTOPLAY_INTERVAL);
    }

    if (prevBtn) { prevBtn.addEventListener('click', function () { stopAuto(); prev(); }); }
    if (nextBtn) { nextBtn.addEventListener('click', function () { stopAuto(); next(); }); }
    Array.prototype.forEach.call(dots, function (dot, i) {
      dot.addEventListener('click', function () { stopAuto(); goTo(i); });
    });

    var hovered = false;
    root.addEventListener('mouseenter', function () { hovered = true; });
    root.addEventListener('mouseleave', function () { hovered = false; });
    window.addEventListener('keydown', function (ev) {
      if (!hovered) { return; }
      if (ev.key === 'ArrowRight') { ev.preventDefault(); stopAuto(); next(); }
      else if (ev.key === 'ArrowLeft') { ev.preventDefault(); stopAuto(); prev(); }
    });

    var touchX = 0;
    root.addEventListener('touchstart', function (ev) { touchX = ev.changedTouches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', function (ev) {
      var dx = ev.changedTouches[0].clientX - touchX;
      if (Math.abs(dx) > SWIPE_THRESHOLD) {
        stopAuto();
        if (dx < 0) { next(); } else { prev(); }
      }
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.35) { startAuto(); }
        });
      }, { threshold: [0, 0.35, 0.7] });
      io.observe(sceneEl);
    } else {
      startAuto();
    }

    render();
  }

  ready(function () {
    var roots = document.querySelectorAll('[data-hiw]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.hiwBound === 'true') { return; }
      root.dataset.hiwBound = 'true';
      initTutorial(root);
    });
  });
})();
